<script setup lang="ts">
/**
 * CardFan — a small hand of tarot cards fanned from a common pivot below the
 * cards: face-down (the deck's own back art, one per card in the spread, so a tile
 * without a cat drawing still says how big the reading is), or face-up when
 * `faces` (static image URLs) is given — the cards a saved reading drew.
 * Static and decorative; card stock via box-shadow (no filters — it sits in grids).
 */
import { computed } from 'vue';
import { useStatic } from '@core/services';

const props = withDefaults(defineProps<{ count?: number; faces?: string[]; cardWidth?: number; spread?: number }>(), {
  count: 1,
  faces: undefined,
  cardWidth: 44,
  spread: 9,
});

const { getStaticUrl } = useStatic();
const back = getStaticUrl('images/moon_magic_tarot/back.jpg');
/** Card faces are 550×966. */
const cardHeight = computed(() => (props.cardWidth * 966) / 550);

const cards = computed(() => {
  const n = Math.max(1, Math.min(7, props.faces?.length || props.count));
  const mid = (n - 1) / 2;
  return Array.from({ length: n }, (_, i) => ({
    key: i,
    src: props.faces?.[i] ?? back,
    style: {
      transform: `translateX(-50%) rotate(${(i - mid) * props.spread}deg)`,
      zIndex: i,
    },
  }));
});
// Width of the whole fan so it can be placed/clipped by its parent.
const fanWidth = computed(() => props.cardWidth + (cards.value.length - 1) * props.cardWidth * 0.42);
</script>

<template>
  <div
    class="card-fan relative"
    :style="{ width: `${fanWidth}px`, height: `${cardHeight * 1.08}px` }"
    aria-hidden="true"
  >
    <img
      v-for="c in cards"
      :key="c.key"
      :src="c.src"
      alt=""
      draggable="false"
      loading="lazy"
      class="cf-card"
      :style="{ ...c.style, width: `${cardWidth}px`, height: `${cardHeight}px` }"
    >
  </div>
</template>

<style scoped>
.cf-card {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform-origin: 50% 160%;
  border-radius: 4px;
  object-fit: cover;
  user-select: none;
  -webkit-user-drag: none;
  /* Same sticker look as TarotCard: cream die-cut ring + soft lift. */
  box-shadow:
    0 0 0 2px var(--paper-stock),
    0 6px 10px -4px var(--paper-shade);
}
</style>
